/*
  home.js — componentes das seções da home (Design Canvas §13, §22)

  Custom elements pequenos, sem framework, todos opcionais: sem JS o
  conteúdo continua acessível (trilhos rolam na mão, abas mostram o
  primeiro painel, o texto expansível aparece inteiro).

  - <amc-slideshow>  banner rotativo: setas, pontos, teclado, sem autoplay
                     por padrão; com autoplay pausa no hover/foco e respeita
                     prefers-reduced-motion.
  - <amc-carrossel>  trilho com scroll-snap + botões anterior/próximo.
  - <amc-abas>       abas acessíveis (role=tablist, setas ←/→, Home/End).
  - <amc-contagem>   contagem regressiva até data-fim; some quando termina.
  - <amc-video>      abre um <dialog> com o vídeo (iframe só é criado ao abrir).
  - <amc-expandir>   texto longo recolhido com "Ler mais / Ler menos".
*/
(() => {
  const reduzMovimento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Slideshow ---------------- */
  if (!customElements.get('amc-slideshow')) {
    class AMCSlideshow extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        if (this.slides.length < 2) return;
        this.pontos = [...this.querySelectorAll('[data-ponto]')];
        this.atual = 0;
        this.intervalo = Number(this.dataset.autoplay || 0) * 1000;

        this.querySelector('[data-anterior]')?.addEventListener('click', () => this.ir(this.atual - 1, true));
        this.querySelector('[data-proximo]')?.addEventListener('click', () => this.ir(this.atual + 1, true));
        this.pontos.forEach((p, i) => p.addEventListener('click', () => this.ir(i, true)));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.ir(this.atual - 1, true); }
          if (e.key === 'ArrowRight') { e.preventDefault(); this.ir(this.atual + 1, true); }
        });

        if (this.intervalo > 0 && !reduzMovimento()) {
          ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => this.pausar()));
          ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => this.tocar()));
          this.tocar();
        }
        this.ir(0, false);
      }

      disconnectedCallback() { this.pausar(); }

      ir(indice, peloUsuario) {
        const total = this.slides.length;
        this.atual = (indice + total) % total;
        this.slides.forEach((s, i) => {
          const ativo = i === this.atual;
          s.classList.toggle('is-ativo', ativo);
          s.setAttribute('aria-hidden', String(!ativo));
          s.querySelectorAll('a, button').forEach((el) => (ativo ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
        });
        this.pontos.forEach((p, i) => p.setAttribute('aria-current', String(i === this.atual)));
        const status = this.querySelector('[data-status]');
        if (status && peloUsuario) status.textContent = `${this.atual + 1} / ${total}`;
      }

      tocar() {
        this.pausar();
        if (this.intervalo > 0) this.timer = setInterval(() => this.ir(this.atual + 1, false), this.intervalo);
      }

      pausar() { clearInterval(this.timer); }
    }
    customElements.define('amc-slideshow', AMCSlideshow);
  }

  /* ---------------- Carrossel (trilho) ---------------- */
  if (!customElements.get('amc-carrossel')) {
    class AMCCarrossel extends HTMLElement {
      connectedCallback() {
        this.trilho = this.querySelector('[data-trilho]');
        this.anterior = this.querySelector('[data-anterior]');
        this.proximo = this.querySelector('[data-proximo]');
        if (!this.trilho) return;
        this.anterior?.addEventListener('click', () => this.mover(-1));
        this.proximo?.addEventListener('click', () => this.mover(1));
        this.trilho.addEventListener('scroll', () => this.atualizar(), { passive: true });
        this.observador = new ResizeObserver(() => this.atualizar());
        this.observador.observe(this.trilho);
        this.atualizar();
      }

      disconnectedCallback() { this.observador?.disconnect(); }

      mover(direcao) {
        const item = this.trilho.firstElementChild;
        const passo = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.trilho).columnGap || 0) : this.trilho.clientWidth;
        this.trilho.scrollBy({ left: direcao * passo, behavior: reduzMovimento() ? 'auto' : 'smooth' });
      }

      atualizar() {
        const max = this.trilho.scrollWidth - this.trilho.clientWidth - 2;
        const rolavel = max > 0;
        this.classList.toggle('is-rolavel', rolavel);
        if (this.anterior) this.anterior.disabled = !rolavel || this.trilho.scrollLeft <= 2;
        if (this.proximo) this.proximo.disabled = !rolavel || this.trilho.scrollLeft >= max;
      }
    }
    customElements.define('amc-carrossel', AMCCarrossel);
  }

  /* ---------------- Abas ---------------- */
  if (!customElements.get('amc-abas')) {
    class AMCAbas extends HTMLElement {
      connectedCallback() {
        this.abas = [...this.querySelectorAll('[role="tab"]')];
        this.paineis = [...this.querySelectorAll('[role="tabpanel"]')];
        if (this.abas.length === 0) return;
        this.abas.forEach((aba, i) => {
          aba.addEventListener('click', () => this.selecionar(i));
          aba.addEventListener('keydown', (e) => {
            const mapa = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: this.abas.length - 1 };
            if (!(e.key in mapa)) return;
            e.preventDefault();
            const alvo = (mapa[e.key] + this.abas.length) % this.abas.length;
            this.selecionar(alvo);
            this.abas[alvo].focus();
          });
        });
        this.selecionar(0);
      }

      selecionar(indice) {
        this.abas.forEach((aba, i) => {
          const ativa = i === indice;
          aba.setAttribute('aria-selected', String(ativa));
          aba.tabIndex = ativa ? 0 : -1;
          if (this.paineis[i]) this.paineis[i].hidden = !ativa;
        });
      }
    }
    customElements.define('amc-abas', AMCAbas);
  }

  /* ---------------- Contagem regressiva ---------------- */
  if (!customElements.get('amc-contagem')) {
    class AMCContagem extends HTMLElement {
      connectedCallback() {
        const texto = (this.dataset.fim || '').trim().replace(' ', 'T');
        this.fim = new Date(texto).getTime();
        if (Number.isNaN(this.fim)) { this.hidden = true; return; }
        this.campos = {
          dias: this.querySelector('[data-dias]'),
          horas: this.querySelector('[data-horas]'),
          minutos: this.querySelector('[data-minutos]'),
          segundos: this.querySelector('[data-segundos]')
        };
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() { clearInterval(this.timer); }

      tick() {
        const resto = Math.max(0, this.fim - Date.now());
        if (resto === 0) {
          clearInterval(this.timer);
          this.hidden = true;
          return;
        }
        const s = Math.floor(resto / 1000);
        const valores = {
          dias: Math.floor(s / 86400),
          horas: Math.floor((s % 86400) / 3600),
          minutos: Math.floor((s % 3600) / 60),
          segundos: s % 60
        };
        Object.entries(valores).forEach(([chave, valor]) => {
          if (this.campos[chave]) this.campos[chave].textContent = String(valor).padStart(2, '0');
        });
        this.hidden = false;
      }
    }
    customElements.define('amc-contagem', AMCContagem);
  }

  /* ---------------- Vídeo em diálogo ---------------- */
  if (!customElements.get('amc-video')) {
    class AMCVideo extends HTMLElement {
      connectedCallback() {
        this.dialogo = this.querySelector('dialog');
        this.moldura = this.querySelector('[data-moldura]');
        this.querySelector('[data-abrir]')?.addEventListener('click', () => this.abrir());
        this.querySelector('[data-fechar]')?.addEventListener('click', () => this.dialogo.close());
        this.dialogo?.addEventListener('close', () => { this.moldura.innerHTML = ''; });
        this.dialogo?.addEventListener('click', (e) => { if (e.target === this.dialogo) this.dialogo.close(); });
      }

      abrir() {
        const modelo = this.querySelector('template');
        if (!modelo || !this.dialogo) return;
        this.moldura.innerHTML = '';
        this.moldura.appendChild(modelo.content.cloneNode(true));
        this.dialogo.showModal();
        this.moldura.querySelector('video')?.play?.();
      }
    }
    customElements.define('amc-video', AMCVideo);
  }

  /* ---------------- Texto expansível ---------------- */
  if (!customElements.get('amc-expandir')) {
    class AMCExpandir extends HTMLElement {
      connectedCallback() {
        this.conteudo = this.querySelector('[data-conteudo]');
        this.botao = this.querySelector('[data-alternar]');
        if (!this.conteudo || !this.botao) return;
        this.classList.add('is-recolhido');
        this.botao.hidden = false;
        if (this.conteudo.scrollHeight <= this.conteudo.clientHeight + 4) {
          this.classList.remove('is-recolhido');
          this.botao.hidden = true;
          return;
        }
        this.botao.addEventListener('click', () => {
          const aberto = this.classList.toggle('is-recolhido') === false;
          this.botao.setAttribute('aria-expanded', String(aberto));
          this.botao.textContent = aberto ? this.botao.dataset.menos : this.botao.dataset.mais;
        });
      }
    }
    customElements.define('amc-expandir', AMCExpandir);
  }
})();
